import { useEffect, useRef, type ReactNode } from 'react';

/**
 * A modal dialog that is actually operable by keyboard.
 *
 * The admin tree is the form-heavy half of Fig and the place these failures bite hardest.
 * Four things this handles that a hand-rolled overlay usually does not:
 *
 *   - focus moves INTO the dialog on open, and back to whatever opened it on close
 *   - Tab is trapped inside while it is open (WCAG 2.1.2 — a dialog you cannot leave by
 *     keyboard is not an inconvenience, it is a trap)
 *   - Escape closes it
 *   - the page behind is inert to a screen reader, so it does not read straight through
 *
 * `<dialog>` gets most of this natively, but its top-layer behaviour and backdrop styling are
 * still inconsistent enough across browsers that the explicit version is easier to trust.
 */
export function Dialog({ open, onClose, title, children }: {
  open: boolean; onClose: () => void; title: string; children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const focusables = () => Array.from(
      panel?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    );
    focusables()[0]?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      returnFocusRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title} ref={panelRef}>
        <h2 className="state-title">{title}</h2>
        {children}
      </div>
    </div>
  );
}

/**
 * A labelled field whose error is tied to the input programmatically.
 *
 * A red border is invisible to a screen reader and to anyone who cannot distinguish the
 * colour. `aria-describedby` is what actually makes the message reach the person, and
 * `autocomplete` is both a WCAG 1.3.5 requirement and one less thing to type.
 */
export function Field({ id, label, error, hint, ...input }: {
  id: string; label: string; error?: string | undefined; hint?: string | undefined;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const describedBy = [error ? `${id}-error` : null, hint ? `${id}-hint` : null]
    .filter(Boolean).join(' ') || undefined;
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        className="input"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...input}
      />
      {hint && <p id={`${id}-hint`} className="hint">{hint}</p>}
      {error && <p id={`${id}-error`} className="hint hint-error" role="alert">{error}</p>}
    </div>
  );
}
